import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { formatErr } from "../lib/api";
import { Logo } from "../components/Header";

export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await login(email, password);
      navigate(loc.state?.redirect || "/dashboard");
    } catch (e) { setError(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-[100svh] grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1738790096021-098eb21bfa11?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400" alt="" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-br from-ink/40 via-ink/30 to-ink/80"/>
        <div className="aurora"/>
        <div className="relative h-full flex flex-col justify-between p-12">
          <Logo/>
          <div>
            <div className="font-script text-5xl text-bone">"{t("auth.login_quote", "Your name. The right key.")}"</div>
            <div className="eyebrow mt-3">{t("auth.login_quote_author", "— Concierge Desk · Ibiza")}</div>
          </div>
        </div>
      </div>
      <div className="grid place-items-center px-5 py-12 md:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Logo/></div>
          <div className="eyebrow mb-3">{t("auth.login_eyebrow", "Sign in")}</div>
          <h1 className="section-title text-4xl mb-2">{t("auth.login_title", "Welcome back.")}</h1>
          <p className="text-muted mb-8 text-sm">{t("auth.login_subtitle", "Sign in to access your bookings, custom trips and concierge desk.")}</p>
          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <input type="email" required placeholder={t("auth.email_placeholder", "Email")} value={email} onChange={e => setEmail(e.target.value)} className="input-bare" data-testid="login-email"/>
            <input type="password" required placeholder={t("auth.password_placeholder", "Password")} value={password} onChange={e => setPassword(e.target.value)} className="input-bare" data-testid="login-password"/>
            {error && (
              error.startsWith("Account suspended") ? (
                <div className="rounded-2xl border border-rose-400/40 bg-rose-500/10 p-4 text-sm" data-testid="login-suspended-banner">
                  <div className="font-semibold text-rose-200 mb-1">{t("auth.suspended_title", "Account suspended")}</div>
                  <div className="text-rose-100/90 text-xs leading-relaxed">
                    {error.replace(/^Account suspended:\s*/i, "")}
                  </div>
                  <div className="text-rose-200/70 text-[11px] mt-2">
                    {t("auth.suspended_appeal", "Contact support to appeal.")} <a href="mailto:support@meetandmint.com" className="underline">support@meetandmint.com</a>
                  </div>
                </div>
              ) : (
                <div className="text-rose-300 text-sm" data-testid="login-error">{error}</div>
              )
            )}
            <button type="submit" disabled={busy} className="btn btn-primary w-full" data-testid="login-submit">
              {busy ? t("auth.signing_in", "Signing in…") : t("common.sign_in", "Sign in")}
            </button>
          </form>
          <div className="text-sm text-muted mt-6">
            {t("auth.new_here", "New here?")} <Link to="/signup" className="text-teal hover:underline" data-testid="login-go-signup">{t("auth.create_account", "Create an account")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
